import { sectionRenderer } from '@theme/section-renderer';

/**
 * Tlačítko „Zobrazit další produkty“ pod výpisem.
 *
 * Téma umí jen dvě krajnosti: nekonečné donačítání při scrollu, nebo čísla stránek.
 * Bernd chtěl tlačítko (Freelo, 14. 9. 2026). Donačítání řeší stejný nástroj, jaký
 * používá `paginated-list.js` — `sectionRenderer.getSectionHTML` vrátí HTML další
 * stránky sekce a z něj se vyberou karty. Rozdíl je jen ve spouštěči: klik místo
 * protnutí neviditelného bodu.
 *
 * Bez JavaScriptu zůstanou v místě tlačítka funkční odkazy na stránky — tlačítko je
 * do té doby `hidden` a čísla stránek se schovají až tehdy, když se komponenta chytí.
 */
class LoadMoreProducts extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-load-more-button]');
    this.pagination = this.querySelector('.pagination');
    this.grid = this.closest('results-list')?.querySelector('[ref="grid"]');

    if (!this.button || !this.grid) return;

    this.pageSize = Number(this.getAttribute('page-size')) || 0;
    this.total = Number(this.getAttribute('total')) || 0;
    this.pagination?.setAttribute('hidden', '');
    this.button.hidden = false;
    this.button.addEventListener('click', this.#load);
    this.#sync();
    this.#restoreEarlierPages();
  }

  /**
   * Kdo se vrátí z produktu zpět, přistane na ?page=3 a viděl by jen dvanáct karet od
   * pětadvacátého produktu — a protože tlačítko schovává čísla stránek, neměl by se jak
   * vrátit na začátek. Chybějící stránky proto doplníme nad mřížku hned při načtení.
   * Dělá se to takhle, a ne pozorovatelem scrollu jako u nekonečného donačítání, protože
   * ten při setrvání na kraji stránky stihne vložit tutéž stránku dvakrát.
   */
  async #restoreEarlierPages() {
    const first = Number(this.#cards[0]?.dataset.page) || 1;
    if (first <= 1) return;

    for (let page = first - 1; page >= 1; page--) {
      const cards = await this.#fetchCards(page);
      if (!cards?.length) break;

      const anchor = this.grid.firstElementChild;
      const before = anchor?.getBoundingClientRect().top ?? 0;
      this.grid.prepend(...cards);
      const after = anchor?.getBoundingClientRect().top ?? 0;
      // Prepend přidá obsah nad výřez, takže by pohled uskočil dolů. Vrátíme ho zpátky.
      window.scrollBy({ top: after - before, behavior: 'instant' });
      this.#sync();
    }
  }

  /**
   * @param {number} page
   * @returns {Promise<Element[]>}
   */
  async #fetchCards(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';

    try {
      // getSectionHTML do předané adresy dopíše section_id, a ta by se pak objevila
      // v adresním řádku. Posíláme proto kopii.
      const html = await sectionRenderer.getSectionHTML(this.getAttribute('section-id'), true, new URL(url));
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return [...parsed.querySelectorAll('[ref="grid"] > [ref="cards[]"]')];
    } catch (error) {
      console.error('Nepodařilo se načíst stránku výpisu', error);
      return [];
    }
  }

  get #lastPage() {
    return Number(this.grid?.dataset.lastPage) || 1;
  }

  get #cards() {
    // Uvnitř mřížky sedí i <script> tématu, proto se počítají jen karty.
    return [...(this.grid?.querySelectorAll(':scope > [data-page]') ?? [])];
  }

  get #currentPage() {
    const cards = this.#cards;
    return Number(cards[cards.length - 1]?.dataset.page) || 1;
  }

  #sync() {
    // Počítá se, co je opravdu v mřížce — po návratu zpět na ?page=3 je v ní zpočátku
    // jen ta jedna stránka, než se doplní předchozí.
    const shown = Math.min(this.#cards.length, this.total);
    if (this.counter) {
      this.counter.textContent = `Zobrazeno ${shown} z ${this.total} produktů`;
      this.counter.hidden = false;
    }

    const done = this.#currentPage >= this.#lastPage;
    this.hidden = done;
    this.button.hidden = done;
  }

  get counter() {
    return this.querySelector('[data-load-more-count]');
  }

  #load = async () => {
    const next = this.#currentPage + 1;
    if (next > this.#lastPage) return this.#sync();

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(next));
    url.hash = '';

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    try {
      const cards = await this.#fetchCards(next);

      if (cards.length) {
        this.grid.append(...cards);
        // Adresu držíme v souladu s tím, co je vidět, ale bez nové položky v historii —
        // tlačítko „zpět“ má vrátit na předchozí stránku webu, ne o dvanáct karet.
        history.replaceState(history.state, '', url.toString());
      }
    } finally {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
      this.#sync();
    }
  };
}

if (!customElements.get('load-more-products')) {
  customElements.define('load-more-products', LoadMoreProducts);
}
